import clsx from 'clsx'
import React, {FC, useEffect, useState} from 'react'
import {Spinner} from 'react-bootstrap'
import {UserSubscriptionPlan} from 'src/interfaces/Platform.interface'
import {KTIcon} from 'src/marketConnect/helpers'
import {
  BillingCycleOrderCountsResponse,
  isBillingCycleOrderCountsSuccess,
  mapBillingCycleOrderCountsFromApiResponse,
} from './billingCycleOrderCountsApi'
import {getBillingCycleOrderCountsAsync, getUserPlanAsync} from 'src/redux/actions/pricingActions'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {isActiveFreeTrial28DaySubscription} from 'src/utils/freeTrialDeferredOnboarding'
import Loader from 'src/utils/Loader'
import InvoiceDetailsGeneration from './InvoiceDetails'
import './PlanAndUsagePage.scss'
import {formatBillingDateTime} from './billingDateFormat'
import {
  getActivateBillingPlanSubscriptionId,
  isPlanAndUsageWelcomeUser,
  splitFreeTrialPlanDisplayTitle,
} from './planUsagePricing.helpers'
import {useFreeTrialDeferredOnboarding} from 'src/hooks/useFreeTrialDeferredOnboarding'
import UpdatedPricingPageV2 from './UpdatedPricingPageV2'

const BILLING_SUPPORT_EMAIL = 'support@mycommercium.com'

type BillingTab = 'PLAN_USAGE' | 'TRANSACTIONS'

export type PlanAndUsagePageProps = {
  /** Wire to MAIN `useFreeTrialDeferredOnboarding().openOnboardingWizard` when route is connected. */
  onContinueWithFreeTrial?: () => void
}

const PlanAndUsagePage: FC<PlanAndUsagePageProps> = ({onContinueWithFreeTrial}) => {
  const dispatch = useAppDispatch()
  const {fetchedUserAccountData, fetchedUserAccountLoading} = useAppSelector(
    (state) => state.userAccountData
  )
  const {userCurrentSubscription} = useAppSelector((state) => state.pricing)
  const [activePlanDetail, setActivePlanDetail] = useState<UserSubscriptionPlan | null>(null)
  const [isOldPrice, setIsOldPrice] = useState(false)
  const [loadingPlan, setLoadingPlan] = useState(true)
  const [ordersLast30, setOrdersLast30] = useState(0)
  const [ordersLast90, setOrdersLast90] = useState(0)
  const [ordersUsageLoading, setOrdersUsageLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<BillingTab>('PLAN_USAGE')
  const [copySubscriptionMsg, setCopySubscriptionMsg] = useState('Copy')

  const isPlanAndUsageWelcome = isPlanAndUsageWelcomeUser(
    fetchedUserAccountData,
    activePlanDetail
  )
  const showTransactionsTab = !isPlanAndUsageWelcome
  const showWelcomeHeader = isPlanAndUsageWelcome
  const isOnFreeTrial = isActiveFreeTrial28DaySubscription(activePlanDetail)

  const accountName = String(fetchedUserAccountData?.name ?? '').trim()
  const displayName =
    accountName || fetchedUserAccountData?.email?.split('@')[0] || 'there'

  useEffect(() => {
    setLoadingPlan(true)
    dispatch(getUserPlanAsync())
      .then((response) => {
        if (response?.payload?.data?.billing_plan) {
          const data = response.payload.data
          setActivePlanDetail({
            ...data,
            user_count: response.payload?.user_count,
          })
          const {user_transactions, current_billing_plan, billing_plan} = data
          const oldPrice =
            current_billing_plan === user_transactions?.billing_plan &&
            billing_plan?.price?.value !== Number(user_transactions?.amount_paid)
          setIsOldPrice(oldPrice)
        } else if (response.payload?.status === 204) {
          setActivePlanDetail(null)
        }
        setLoadingPlan(false)
      })
      .catch(() => setLoadingPlan(false))
  }, [dispatch])

  useEffect(() => {
    if (isPlanAndUsageWelcome) {
      return
    }

    let cancelled = false

    const fetchOrderCounts = async () => {
      setOrdersUsageLoading(true)
      try {
        const result = await dispatch(getBillingCycleOrderCountsAsync())
        if (cancelled) {
          return
        }
        const payload = result?.payload as BillingCycleOrderCountsResponse | undefined
        if (isBillingCycleOrderCountsSuccess(payload)) {
          const counts = mapBillingCycleOrderCountsFromApiResponse(payload)
          setOrdersLast30(counts.ordersLast30)
          setOrdersLast90(counts.ordersLast90)
        } else {
          setOrdersLast30(0)
          setOrdersLast90(0)
        }
      } catch {
        if (!cancelled) {
          setOrdersLast30(0)
          setOrdersLast90(0)
        }
      } finally {
        if (!cancelled) {
          setOrdersUsageLoading(false)
        }
      }
    }

    fetchOrderCounts()

    return () => {
      cancelled = true
    }
  }, [dispatch, isPlanAndUsageWelcome])

  const scrollToPlans = () => {
    document.getElementById('pricing-plans-section')?.scrollIntoView({behavior: 'smooth'})
  }

  const formatPrice = () => {
    if (!activePlanDetail?.billing_plan) return '—'
    const plan = activePlanDetail.billing_plan
    const amount = isOldPrice
      ? Number(activePlanDetail.user_transactions?.amount_paid)
      : plan.showcase_price?.value ?? plan.price?.value
    const currency = plan.showcase_price?.currency_iso || plan.price?.currency_iso || 'USD'
    const period =
      plan.billing_frequency === 'ANNUAL' || plan.billing_frequency === 'YEARLY'
        ? 'year'
        : 'month'
    const symbol = currency === 'USD' ? '$' : ''
    return `${symbol}${Number(amount).toFixed(2)} / ${period}`
  }

  const orderLimit = activePlanDetail?.billing_plan?.order_limit
  const skuLimit = activePlanDetail?.billing_plan?.product_limit
  const subscriptionPlan = activePlanDetail ?? userCurrentSubscription
  const subscriptionId = getActivateBillingPlanSubscriptionId(subscriptionPlan)
  const showSubscriptionId = !isPlanAndUsageWelcome && Boolean(subscriptionId)
  const planTitleParts = isOnFreeTrial
    ? splitFreeTrialPlanDisplayTitle(activePlanDetail?.billing_plan?.display_name)
    : {
        primary: activePlanDetail?.billing_plan?.display_name ?? '',
        secondary: null as string | null,
      }

  if (loadingPlan || fetchedUserAccountLoading) {
    return <Loader />
  }

  return (
    <div className='page--plan-and-usage'>
      <div className='page--plan-and-usage__inner'>
        <header className='page--plan-and-usage__header'>
          <div>
            {showWelcomeHeader ? (
              <>
                <h1 className='page--plan-and-usage__title'>
                  Welcome to Commercium, {displayName}
                </h1>
                <p className='page--plan-and-usage__subtitle'>
                  Your 28-day free trial is active. No credit card needed. Start connecting your
                  channels, or pick a plan whenever you&apos;re ready.
                </p>
              </>
            ) : (
              <>
                <h1 className='page--plan-and-usage__title'>Plan &amp; billing</h1>
                <p className='page--plan-and-usage__subtitle'>
                  Manage your subscription, check usage and download invoices.
                </p>
              </>
            )}
          </div>
          <div className='page--plan-and-usage__actions'>
            <a
              href={`mailto:${BILLING_SUPPORT_EMAIL}`}
              className='page--plan-and-usage__help-link'
            >
              <i className='bi bi-question-circle' aria-hidden='true' />
              Help with billing
            </a>
            {showWelcomeHeader ? (
              <button
                type='button'
                className='page--plan-and-usage__connect-btn'
                onClick={onContinueWithFreeTrial}
              >
                Connect your first channel
                <svg
                  width='18'
                  height='18'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.25'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <path d='M5 12h14' />
                  <path d='m12 5 7 7-7 7' />
                </svg>
              </button>
            ) : null}
          </div>
        </header>

        <nav className='page--plan-and-usage__tabs' aria-label='Billing sections'>
          <button
            type='button'
            className={clsx('page--plan-and-usage__tab', {
              'page--plan-and-usage__tab--active': activeTab === 'PLAN_USAGE',
            })}
            onClick={() => setActiveTab('PLAN_USAGE')}
          >
            Plan &amp; usage
          </button>
          {showTransactionsTab ? (
            <button
              type='button'
              className={clsx('page--plan-and-usage__tab', {
                'page--plan-and-usage__tab--active': activeTab === 'TRANSACTIONS',
              })}
              onClick={() => setActiveTab('TRANSACTIONS')}
            >
              Transactions
            </button>
          ) : null}
        </nav>

        {activeTab === 'PLAN_USAGE' ? (
          <>
            {activePlanDetail ? (
              <section
                className='page--plan-and-usage__current-card'
                aria-labelledby='pu-current-plan'
              >
                <div className='page--plan-and-usage__current-head'>
                  <div className='page--plan-and-usage__plan-info'>
                    <div className='page--plan-and-usage__eyebrow-row'>
                      <span className='page--plan-and-usage__eyebrow'>Active plan</span>
                      {activePlanDetail.isActive ? (
                        <span className='page--plan-and-usage__badge-active'>Active</span>
                      ) : null}
                    </div>
                    <h2 id='pu-current-plan' className='page--plan-and-usage__plan-title'>
                      {planTitleParts.primary}
                      {planTitleParts.secondary ? (
                        <span className='page--plan-and-usage__plan-title-muted'>
                          {' '}
                          {planTitleParts.secondary}
                        </span>
                      ) : null}
                    </h2>
                    {!isPlanAndUsageWelcome ? (
                      <div className='page--plan-and-usage__plan-includes'>
                        <span className='page--plan-and-usage__plan-includes-label'>
                          Plan includes
                        </span>
                        <div className='page--plan-and-usage__plan-includes-badges'>
                          <span className='page--plan-and-usage__plan-limit-badge'>
                            <KTIcon
                              iconName='purchase'
                              iconType='outline'
                              className='page--plan-and-usage__plan-limit-badge-icon'
                            />
                            {orderLimit?.toLocaleString() ?? '—'} orders
                          </span>
                          <span className='page--plan-and-usage__plan-limit-badge'>
                            <KTIcon
                              iconName='price-tag'
                              iconType='outline'
                              className='page--plan-and-usage__plan-limit-badge-icon'
                            />
                            {skuLimit?.toLocaleString() ?? '—'} SKUs
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className='page--plan-and-usage__plan-actions'>
                    <button
                      type='button'
                      className={
                        showWelcomeHeader
                          ? 'page--plan-and-usage__upgrade-btn'
                          : 'page--plan-and-usage__upgrade-btn-primary'
                      }
                      onClick={scrollToPlans}
                    >
                      {showWelcomeHeader ? 'See upgrade options' : 'Upgrade plan'}
                    </button>
                  </div>
                </div>

                {!isPlanAndUsageWelcome ? (
                  <div className='page--plan-and-usage__usage'>
                    <div className='page--plan-and-usage__stat'>
                      <span className='page--plan-and-usage__metric-label'>
                        Orders · last 30 days
                      </span>
                      <p className='page--plan-and-usage__stat-value'>
                        <span className='page--plan-and-usage__metric-value'>
                          {ordersUsageLoading ? (
                            <Spinner animation='border' size='sm' />
                          ) : (
                            ordersLast30.toLocaleString()
                          )}
                        </span>
                      </p>
                    </div>
                    <div className='page--plan-and-usage__stat'>
                      <span className='page--plan-and-usage__metric-label'>
                        Orders · last 90 days
                      </span>
                      <p className='page--plan-and-usage__stat-value'>
                        <span className='page--plan-and-usage__metric-value'>
                          {ordersUsageLoading ? (
                            <Spinner animation='border' size='sm' />
                          ) : (
                            ordersLast90.toLocaleString()
                          )}
                        </span>
                      </p>
                    </div>
                  </div>
                ) : null}

                <div className='page--plan-and-usage__details'>
                  <span className='page--plan-and-usage__detail-item'>
                    Billing Price{' '}
                    <strong className='page--plan-and-usage__subscription-strong'>
                      {formatPrice()}
                    </strong>
                  </span>
                  {activePlanDetail.activated_on ? (
                    <span className='page--plan-and-usage__detail-item'>
                      Started{' '}
                      <strong className='page--plan-and-usage__subscription-strong'>
                        {formatBillingDateTime(activePlanDetail.activated_on)}
                      </strong>
                    </span>
                  ) : null}
                  {isPlanAndUsageWelcome ? (
                    <span className='page--plan-and-usage__detail-item'>
                      Limit{' '}
                      <strong className='page--plan-and-usage__subscription-strong'>
                        {orderLimit?.toLocaleString() ?? '—'} Orders /{' '}
                        {skuLimit?.toLocaleString() ?? '—'} SKUs
                      </strong>
                    </span>
                  ) : null}
                  {activePlanDetail.valid_till ? (
                    <span className='page--plan-and-usage__detail-item'>
                      Valid Till{' '}
                      <strong className='page--plan-and-usage__subscription-strong'>
                        {formatBillingDateTime(activePlanDetail.valid_till)}
                      </strong>
                    </span>
                  ) : null}
                  {showSubscriptionId ? (
                    <span className='page--plan-and-usage__detail-item page--plan-and-usage__subscription-id'>
                      <span className='page--plan-and-usage__subscription-id-label'>
                        Subscription ID
                      </span>
                      <span className='page--plan-and-usage__subscription-id-group'>
                        <strong
                          className='page--plan-and-usage__subscription-strong page--plan-and-usage__subscription-id-value'
                          id='pu-subscription-id'
                        >
                          {subscriptionId}
                        </strong>
                        <button
                          type='button'
                          className='page--plan-and-usage__copy-id'
                          aria-label='Copy subscription ID'
                          onClick={() => {
                            navigator.clipboard.writeText(subscriptionId!)
                            setCopySubscriptionMsg('Copied')
                          }}
                          onMouseLeave={() => setCopySubscriptionMsg('Copy')}
                        >
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            aria-hidden='true'
                          >
                            <rect x='9' y='9' width='13' height='13' rx='2' />
                            <path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' />
                          </svg>
                        </button>
                        {copySubscriptionMsg === 'Copied' ? (
                          <span className='page--plan-and-usage__copy-feedback'>Copied</span>
                        ) : null}
                      </span>
                    </span>
                  ) : null}
                </div>
              </section>
            ) : null}

            <section
              className='page--plan-and-usage__plans-section'
              aria-labelledby='pu-available-plans-heading'
            >
              <div className='page--plan-and-usage__plans-wrap'>
              <UpdatedPricingPageV2
                planSectionOnly
                hidePlanSectionHeading
                excludeFreePlanFromUpgradeList
                isPlanAndUsageWelcomeUser={isPlanAndUsageWelcome}
                onContinueWithFreeTrial={onContinueWithFreeTrial}
              />
              </div>
            </section>
          </>
        ) : (
          <div className='page--plan-and-usage__transactions-panel'>
            <div className='card page--plan-and-usage__invoice-details' style={{borderRadius: '5px'}}>
              <InvoiceDetailsGeneration />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export const BillingPlanAndUsageRoute = () => {
  const {openOnboardingWizard} = useFreeTrialDeferredOnboarding()

  return <PlanAndUsagePage onContinueWithFreeTrial={openOnboardingWizard} />
}

export default PlanAndUsagePage
